/**
 * Auth API surface — typed wrappers around POST /v1/auth/*. All return
 * shapes match CP-01 §D.
 */
import { apiClient } from "./apiClient";
import { useAuthStore, type AuthUser } from "../stores/auth";

export interface AuthTenant {
  id: string;
  name: string;
  plan_tier: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  name?: string;
  tenant_name: string;
  accept_terms: boolean;
}

export interface RegisterResult {
  user: AuthUser;
  tenant: AuthTenant;
  verification_required: boolean;
  verification_email_sent_to: string;
  /** 64-char URL token. Present only in test/dev environments. */
  verification_token?: string | null;
  /** 6-char short code. Present only in test/dev environments. */
  verification_code?: string | null;
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface LoginResult {
  token: string;
  token_type: "Bearer";
  user: AuthUser;
  tenant: AuthTenant;
}

interface Wrapped<T> {
  data: T;
}

export async function registerAccount(
  input: RegisterInput,
): Promise<RegisterResult> {
  const res = await apiClient.post<Wrapped<RegisterResult>>(
    "/v1/auth/register",
    input,
  );
  return res.data;
}

export async function login(input: LoginInput): Promise<LoginResult> {
  const res = await apiClient.post<Wrapped<LoginResult>>(
    "/v1/auth/login",
    input,
  );
  // Persist the returned session in the auth store so the apiClient
  // picks the token up on subsequent calls.
  useAuthStore.getState().setSession({
    token: res.data.token,
    user: res.data.user,
  });
  return res.data;
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post("/v1/auth/logout");
  } finally {
    useAuthStore.getState().clear();
  }
}

/**
 * Verify by URL token (the unique 64-char string from the magic link).
 */
export async function verifyEmailByToken(token: string): Promise<void> {
  await apiClient.post("/v1/auth/verify-email", { token });
}

/**
 * Verify by short code + email — what the user types into the form.
 * The code is 6 chars; email scopes the lookup so codes can't collide.
 */
export async function verifyEmailByCode(input: {
  code: string;
  email: string;
}): Promise<void> {
  await apiClient.post("/v1/auth/verify-email", {
    code: input.code.toUpperCase().trim(),
    email: input.email,
  });
}

export async function resendVerification(email: string): Promise<void> {
  await apiClient.post("/v1/auth/resend-verification", { email });
}

export async function forgotPassword(
  email: string,
): Promise<{ sent: true; reset_token?: string | null }> {
  const res = await apiClient.post<
    Wrapped<{ sent: true; reset_token?: string | null }>
  >("/v1/auth/forgot-password", { email });
  return res.data;
}

export async function resetPassword(input: {
  email: string;
  token: string;
  password: string;
}): Promise<void> {
  await apiClient.post("/v1/auth/reset-password", input);
}
